import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { Instagram, Facebook, Mail, MapPin, Phone } from "lucide-react";
import BrandLogo from "./BrandLogo";
import CertBadges from "./CertBadges";

// TikTok icon (lucide doesn't have one in this version)
const TikTokIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-5.86 11.93 6.84 6.84 0 0 0 11.6-4.93V9.85a8.16 8.16 0 0 0 4.77 1.52V8a4.85 4.85 0 0 1-1.28-.13Z" />
  </svg>
);

export default function Footer() {
  const { t } = useLang();
  return (
    <footer className="bg-black text-primary-foreground relative overflow-hidden" data-testid="footer">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
           style={{ backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/c612f9b0-bd89-4c85-9451-5b81c8951306/images/165161635b2ebd7638221dd2398e9e8e9b9cc0c1d356138bcff47de1dc799d9f.png')", backgroundSize: "300px" }} />

      <div className="container-x relative py-20 lg:py-28">
        {/* Big CTA banner */}
        <div className="mb-20 max-w-3xl">
          <p className="overline mb-6">{t("footer.bigtitle") ? "" : ""}Let&apos;s Talk</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-balance">
            {t("footer.bigtitle")}
          </h2>
          <p className="mt-6 text-white/70 text-lg max-w-xl">{t("footer.bigsub")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="https://wa.me/6282114294101"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-accent"
              data-testid="footer-whatsapp-cta"
            >
              <Phone className="h-4 w-4" /> WhatsApp +62 821-1429-4101
            </a>
            <Link to="/contact" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-primary" data-testid="footer-contact-cta">
              {t("nav.contact")}
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 border-t border-white/15 pt-12">
          <div className="md:col-span-5">
            <div className="mb-5">
              <BrandLogo layout="horizontal" variant="light" className="h-24 w-auto" />
            </div>
            <p className="text-white/65 leading-relaxed max-w-sm text-sm">{t("footer.tagline")}</p>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-serif text-base mb-4 text-accent">{t("footer.explore")}</h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li><Link to="/" className="link-underline">{t("nav.home")}</Link></li>
              <li><Link to="/about" className="link-underline">{t("nav.about")}</Link></li>
              <li><Link to="/packages" className="link-underline">{t("nav.packages")}</Link></li>
              <li><Link to="/haji-khusus" className="link-underline">Haji Khusus</Link></li>
              <li><Link to="/blog" className="link-underline">{t("nav.blog")}</Link></li>
              <li><Link to="/contact" className="link-underline">{t("nav.contact")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-serif text-base mb-4 text-accent">{t("footer.contactInfo")}</h4>
            <ul className="space-y-4 text-sm text-white/75">
              <li className="flex gap-3">
                <MapPin className="h-4 w-4 mt-1 shrink-0 text-accent" />
                <span>Jaticempaka, Pondok Gede, Bekasi, Jawa Barat</span>
              </li>
              <li className="flex gap-3 items-start" data-testid="footer-cs-list">
                <Phone className="h-4 w-4 mt-1 shrink-0 text-accent" />
                <div className="space-y-1">
                  <p className="text-[10px] uppercase tracking-widest text-white/60">Customer Service</p>
                  {[
                    { name: "Hasbi",   number: "6282114294101", display: "+62 821-1429-4101" },
                    { name: "Yeyet",   number: "6281932075242", display: "+62 819-3207-5242" },
                    { name: "Hasunah", number: "6289508444277", display: "+62 895-0844-4277" },
                    { name: "Alfi",    number: "62898863793",   display: "+62 898-8637-933"  },
                  ].map((cs) => (
                    <a
                      key={cs.number}
                      href={`https://wa.me/${cs.number}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block link-underline"
                      data-testid={`footer-wa-${cs.name.toLowerCase()}`}
                    >
                      {cs.name} — {cs.display}
                    </a>
                  ))}
                </div>
              </li>
              <li className="flex gap-3 items-start">
                <Mail className="h-4 w-4 mt-1 shrink-0 text-accent" />
                <a href="mailto:batikumrahhaji.bekasi@gmail.com" className="link-underline break-all">
                  batikumrahhaji.bekasi@gmail.com
                </a>
              </li>
            </ul>

            <div className="mt-6">
              <p className="overline mb-3">{t("contact.social")}</p>
              <div className="flex gap-3">
                <a
                  href="https://instagram.com/batikumrah.bekasi"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-accent hover:text-primary transition-colors"
                  data-testid="social-instagram"
                >
                  <Instagram className="h-4 w-4" />
                </a>
                <a
                  href="https://tiktok.com/@batikumrah.bekasi"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="TikTok"
                  className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-accent hover:text-primary transition-colors"
                  data-testid="social-tiktok"
                >
                  <TikTokIcon />
                </a>
                <a
                  href="https://facebook.com/batikumrah.bekasi"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-accent hover:text-primary transition-colors"
                  data-testid="social-facebook"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-10 border-t border-white/15">
          <p className="overline mb-5 !text-accent">Sertifikasi &amp; Akreditasi</p>
          <CertBadges size="h-16" />
        </div>

        <div className="mt-10 pt-6 border-t border-white/15 flex flex-col md:flex-row gap-3 justify-between text-xs text-white/55">
          <span>© {new Date().getFullYear()} PT Baitullah Tiga Kharisma · BATIK Tour &amp; Travel. {t("footer.rights")}</span>
          <span>PPIU No. 91205097027470003 · PIHK No. 91205097027470005 · Akreditasi A</span>
        </div>
      </div>
    </footer>
  );
}
